import React, { useEffect, useState } from "react";
import { ethers } from "ethers";
import Dashboard from "./components/Dashboard";
import Footer from './components/Footer';
import { CONTRACT_ADDRESS, USDT_ADDRESS } from "./utils";
import USDT_ABI from './USDTABI.json';
import "./App.css";
import ScrollToTopButton from "./components/ScrollToTopButton";
import { Magic } from "magic-sdk";
import MagicAccountManager from "./components/MagicAccountManager";
import WikiModal from './components/WikiModal';
import Blog from "./components/Blog";
import { ADMIN_ADDRESS } from "./utils";
import Modal from "./components/Modal";


/* const magic = new Magic('MAGIC_API', {
  network: {
    rpcUrl: "https://data-seed-prebsc-1-s1.binance.org:8545",
    chainId: 97
  }
}); */

function App() {
  const [wallet, setWallet] = useState(null);
  const [usdtBalance, setUsdtBalance] = useState(null);
  const [contractBalance, setContractBalance] = useState("0");
  const [refPool, setRefPool] = useState(null);
  const disconnectWallet = () => { setWallet(null); setUsdtBalance(null); setContractBalance("0"); };
  const [hovering, setHovering] = useState(false);
  const [chainId, setChainId] = useState(null);
  const [userEmail, setUserEmail] = useState("");
  const [showWiki, setShowWiki] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);

  useEffect(() => {
    if (!window.ethereum) return;
    // read it once on mount
    window.ethereum
      .request({ method: "eth_chainId" })
      .then((hex) => setChainId(parseInt(hex, 16)));

    // and listen for any switch
    function handleChainChanged(hexChainId) {
      setChainId(parseInt(hexChainId, 16));
    }
    window.ethereum.on("chainChanged", handleChainChanged);

    return () => {
      window.ethereum.removeListener("chainChanged", handleChainChanged);
    };
  }, []);


  const fetchContractBalance = async () => {
    try {
      const usdt = new ethers.Contract(
        USDT_ADDRESS,
        ["function balanceOf(address account) view returns (uint256)"],
        wallet.provider
      );
      const raw = await usdt.balanceOf(CONTRACT_ADDRESS);
      const full = ethers.formatUnits(raw, 18);
      const formatted = parseFloat(full).toFixed(2);
      setContractBalance(formatted);
    } catch (err) {
      console.error("Error fetching contract balance:", err);
      setContractBalance("0");
    }
  };

  const fetchUserUSDTBalance = async () => {
    try {
      const usdtContract = new ethers.Contract(USDT_ADDRESS, USDT_ABI, wallet.provider);
      const raw = await usdtContract.balanceOf(wallet.address);
      const full = ethers.formatUnits(raw, 18);
      const formattedBalance = parseFloat(full).toFixed(4);
      setUsdtBalance(formattedBalance);
    } catch (error) {
      console.error("Error updating USDT balance:", error);
      setUsdtBalance("0");
    }
  };

  const loginWithMagic = async () => {
    const email = window.prompt("Enter your email to get a magic link:");
    if (!email) return;

    // send magic link
    await magic.auth.loginWithMagicLink({ email });
    setUserEmail(email);

    const provider = new ethers.BrowserProvider(magic.rpcProvider);
    const signer = await provider.getSigner();
    const address = await signer.getAddress();

    setWallet({ provider, signer, address, isMagic: true });
    const net = await provider.getNetwork();
    setChainId(net.chainId);

    try {
      const usdtContract = new ethers.Contract(USDT_ADDRESS, USDT_ABI, provider);
      const bal = await usdtContract.balanceOf(address);
      setUsdtBalance(ethers.formatUnits(bal, 18));
    } catch {
      setUsdtBalance("0");
    }
  };

  async function switchToBscMainnet() {
    if (!window.ethereum) {
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      if (isSafari) {
        setShowDownloadModal(true);
      } else
        setShowDownloadModal(true);
      return;
    }

    try {
      await window.ethereum.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: "0x38" }],
      });
      const provider = new ethers.BrowserProvider(window.ethereum);
      const net = await provider.getNetwork();
      setChainId(net.chainId);
      await window.ethereum.request({
        method: "wallet_switchEthereumChain",
        params: [{ chainId: "0x38" }],
      });
      setChainId(56);
    } catch (err) {
      console.error("Switch to Mainnet failed", err);
      alert("Please switch your wallet to BSC Mainnet.");
      throw err;
    }
  }

  const connectWallet = async () => {
    if (window.ethereum) {
      const provider = new ethers.BrowserProvider(window.ethereum);
      const { chainId } = await provider.getNetwork();
      const network = await provider.getNetwork();
      setChainId(network.chainId);

      if (network.chainId !== 56) {
        try {
          await switchToBscMainnet();
        } catch {
          return;
        }
      }

      if (chainId !== 56) {
        try {
          await window.ethereum.request({
            method: "wallet_switchEthereumChain",
            params: [{ chainId: "0x38" }],
          });
        } catch (switchError) {
          if (switchError.code === 4902) {
            try {
              await window.ethereum.request({
                method: "wallet_addEthereumChain",
                params: [{
                  chainId: "0x38",
                  chainName: "Binance Smart Chain Mainnet",
                  rpcUrls: ["https://bsc-dataseed.binance.org/"],
                  nativeCurrency: {
                    name: "Binance Coin",
                    symbol: "BNB",
                    decimals: 18,
                  },
                  blockExplorerUrls: ["https://bscscan.com"],
                }],
              });
            } catch (addError) {
              alert("Failed to add BSC Mainnet. Please try manually.");
              return;
            }
          } else {
            alert("Please switch to BSC Mainnet in your wallet.");
            return;
          }
        }
      }

      const disconnectWallet = () => {
        setWallet(null);
        setUserEmail("");
        setUsdtBalance(null);
        setContractBalance("0");
      };

      await provider.send("eth_requestAccounts", []);
      const signer = await provider.getSigner();
      const address = await signer.getAddress();
      setWallet({ provider, signer, address, isMagic: false });

      try {
        const usdtContract = new ethers.Contract(USDT_ADDRESS, USDT_ABI, provider);
        const raw = await usdtContract.balanceOf(address);
        const full = ethers.formatUnits(raw, 18);
        const formattedBalance = parseFloat(full).toFixed(4);
        console.log("USDT Balance:", formattedBalance);
        setUsdtBalance(formattedBalance);
      } catch (error) {
        console.error("Error fetching USDT balance:", error);
        alert("Error fetching USDT balance. Check the contract address or network.");
      }
    } else {
      alert("Please install MetaMask to continue.");
    }
  };

  useEffect(() => {
    if (wallet) {
      fetchContractBalance();
      fetchUserUSDTBalance();
      const interval = setInterval(() => {
        fetchContractBalance();
        fetchUserUSDTBalance();
      }, 10000);
      return () => clearInterval(interval);
    }
  }, [wallet]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const ref = params.get("ref");
    if (ref && ref.includes("-")) {
      const [levelStr, localIdStr] = ref.split("-");
      const level = parseInt(levelStr);
      const localId = parseInt(localIdStr);
      if (!isNaN(level) && !isNaN(localId)) {
        setRefPool({ level, localId });
      }
    }
  }, []);

  return (
    <div className="pattern-bg">
      {/* HEADER BOX */}
      <div className="header-frame">
        <h1>Welcome to <br />Global Savings Pools!</h1>
        <p>
          💸 Just 16 USDT can unlock massive savings potential. <br /><br />
          🤝 Get 3 trusted friends, close your pools, and earn big —
          then return the favor as the circle continues! <br /><br />
          🔁 <strong>Give, grow, and gain together. It’s your turn!</strong>
        </p>
        {/* in your header-frame JSX: */}
        {!wallet && chainId !== 56 ? (
          // 1) User not connected, but on wrong chain
          <button
            onClick={switchToBscMainnet}
            className="switch-network-btn"
          >
            ⚙️ Switch to BSC Mainnet
          </button>
        ) : (
          // 2) Either already on BSC (wallet may or may not be set) OR already connected
          <button
            onClick={wallet ? disconnectWallet : connectWallet}
            className={wallet ? "wallet-status-btn" : "connect-wallet-bait"}
          >
            {wallet
              ? "✅ Wallet Connected"
              : "🔐 Connect Wallet"}
          </button>
        )}
        <div className="wallet-help">
          <span></span>
          <button
            className="wallet"
            onClick={() => {
              if (wallet) {
                disconnectWallet()
              } else {
                setShowDownloadModal(true)
              }
            }}
          >
            {wallet ? 'Disconnect Wallet' : "Don't have a Wallet?"}
          </button>
        </div>

        {showDownloadModal && (
          <div className="modal-overlay">
            <div className="modal-content">
              <button className="modal-close-x" onClick={() => setShowDownloadModal(false)}>
                ×
              </button>
              <h3 className="modal-title">Get Started: Install a Wallet</h3>
              <p>To interact with Global Savings Pools you'll need an Ethereum-compatible wallet. We support:</p>
              <ul className="wallet-list">
                <li>
                  <img src="/icons/metamask.png" alt="MetaMask" className="wallet-icon" />
                  <a
                    className="wallet-link"
                    href="https://metamask.io/download"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    MetaMask
                  </a>
                </li>
                <li>
                  <img src="/icons/trustwallet.png" alt="Trust Wallet" className="wallet-icon" />
                  <a
                    className="wallet-link"
                    href="https://trustwallet.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Trust Wallet
                  </a>
                </li>
                <li>
                  <img src="/icons/coinbase.png" alt="Coinbase Wallet" className="wallet-icon" />
                  <a
                    className="wallet-link"
                    href="https://wallet.coinbase.com/"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Coinbase Wallet
                  </a>
                </li>
              </ul>
              <hr />
              <h4>Add BNB Smart Chain Mainnet</h4>
              <p>
                If you've installed your wallet but don't see BNB Smart Chain in the network list,
                click below to add it automatically:
              </p>
              <button
                className="btn-primary mb-4"
                onClick={async () => {
                  try {
                    await window.ethereum.request({
                      method: 'wallet_addEthereumChain',
                      params: [{
                        chainId: '0x38',
                        chainName: 'BNB Smart Chain',
                        rpcUrls: ['https://bsc-dataseed.binance.org/'],
                        nativeCurrency: { name: 'BNB', symbol: 'BNB', decimals: 18 },
                        blockExplorerUrls: ['https://bscscan.com']
                      }]
                    });
                  } catch (err) {
                    console.error('Add network error', err);
                  }
                }}
              >
                Add BNB Mainnet
              </button>

              <h4>Need Help?</h4>
              <p>
                • Read our Wiki.<br />
                • Watch: <a className="wallet-link" href="https://www.youtube.com/watch?v=ePcaRPCP8Rs" target="_blank">Video Tutorial</a><br />
                • Questions? Drop a comment in the Blog.
              </p>

              <button
                className="btn-primary mt-6"
                onClick={() => setShowDownloadModal(false)}
              >
                Close
              </button>
            </div>
          </div>
        )}
        {/* {!wallet && (
          <button
            onClick={loginWithMagic}
            className="connect-magic-bait"
            style={{ marginLeft: 12 }}
          >
            ✉️ Login With Email
          </button>
        )}

        {wallet?.isMagic && (
          <MagicAccountManager
            email={userEmail}
            chainId={chainId}
            isMagic={wallet.isMagic}
            onSwitchNetwork={switchToBscMainnet}
            onChangeEmail={loginWithMagic}
            onLogout={disconnectWallet}
          />
        )} */}

      </div>
      {/* CONTENT BOX */}
      <div className="main-frame">
        {wallet && (
          <Dashboard
            wallet={wallet}
            refPool={refPool}
            usdtBalance={usdtBalance}
            contractBalance={contractBalance}
          />
        )}
        <>
          <button className="wiki-open-btn" onClick={() => setShowWiki(true)}>
            📚 Wiki
          </button>
          <WikiModal show={showWiki} onClose={() => setShowWiki(false)} />
        </>
        {wallet && (
          <Blog
            walletAddress={wallet.address}
            adminAddress={ADMIN_ADDRESS}
          />
        )}
        <Footer />
        <ScrollToTopButton />
      </div>
    </div>
  );
}

export default App;